import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { AlertCircle } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tab, TabList, Tabs } from "@/components/application/tabs/tabs";
import { Avatar } from "@/components/base/avatar/avatar";
import { BadgeGroup } from "@/components/base/badges/badge-groups";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { Illustration } from "@/components/shared-assets/illustrations";
import { DrawerProvider, useDrawerStore } from "@/contexts/drawer-context";
import { usePlatformUserEditAccess } from "@/features/users/platform-user-edit-access";
import { useEditOrgUserDetailsForm } from "@/forms/user/organizationUser/details/edit-org-user-details.form.hooks";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import { userQueryOptions } from "@/services/user";
import { getUserInitials } from "@/utils/get-user-initials";

export const ModeSchema = z.enum(["edit", "view"]);
export const TabSchema = z.enum(["details", "activity", "organizations"]);

export const SearchSchema = z.object({
	mode: ModeSchema.optional().default("view"),
	tab: TabSchema.optional(),
	organizationId: z.string().optional(), // Active organization ID for editing
});

export type Mode = z.infer<typeof ModeSchema>;
export type Tab = z.infer<typeof TabSchema>;
export type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId",
)({
	component: RouteComponent,
	params: {
		parse: (params) => ({
			userId: z.string().parse(params.userId),
		}),
	},
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

export const statusColors = {
	Active: "success",
	Suspended: "warning",
	Archived: "gray",
	Retired: "error",
} as const;

function RouteComponent() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId",
	});
	const search = useSearch({
		from: "/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId",
	});
	const { access: editAccess, isPending: editAccessPending } =
		usePlatformUserEditAccess(params.userId);
	const canEditDetails = editAccess.canEdit && !editAccessPending;
	const [isOpen, setIsOpen] = useState(false);
	const [showOverlay, setShowOverlay] = useState(false);
	const [isOverlayAnimating, setIsOverlayAnimating] = useState(false);

	// Extract current tab from the pathname
	const currentTab = (location.pathname.split("/").pop() as Tab) || "details";

	const userById = useQuery(userQueryOptions.detail(params.userId));
	const { src: userAvatarSrc, isLoading: isAvatarLoading } = useUserAvatarSrc(
		userById.data?.id,
		userById.data?.hasAvatar,
	);

	// Only show initials when not loading and no avatar
	const initials =
		!isAvatarLoading && !userAvatarSrc
			? getUserInitials(userById.data?.firstName, userById.data?.lastName)
			: undefined;

	// Open drawer when component mounts (route is accessed)
	useEffect(() => {
		setIsOpen(true);
	}, []);

	// Handle overlay animation based on mode changes
	useEffect(() => {
		if (
			search.mode === "edit" &&
			canEditDetails &&
			isOpen &&
			currentTab !== "details"
		) {
			// Fade in overlay when entering edit mode
			setShowOverlay(true);
			setIsOverlayAnimating(true);
			// Remove animation class after animation completes
			const timer = setTimeout(() => {
				setIsOverlayAnimating(false);
			}, 300);
			return () => clearTimeout(timer);
		} else if (search.mode === "view" && showOverlay) {
			// Fade out overlay when exiting edit mode
			setIsOverlayAnimating(true);
			// const timer = setTimeout(() => {
			setShowOverlay(false);
			setIsOverlayAnimating(false);
			// }, 300);
			// return () => clearTimeout(timer);
		}
	}, [canEditDetails, currentTab, search.mode, isOpen, showOverlay]);

	// Handle drawer close - navigate back to parent route
	const handleClose = () => {
		setIsOpen(false);
		// Delay navigation to allow exit animation to complete (500ms)
		setTimeout(() => {
			navigate({
				to: `/admin/users/organization`,
				replace: true,
			});
		}, 200);
	};

	// Handle drawer state change
	const handleOpenChange = (open: boolean) => {
		// Prevent closing when in edit mode (persistent)
		if (
			!open &&
			search.mode === "edit" &&
			canEditDetails &&
			currentTab !== "details"
		) {
			return; // Don't close the drawer
		}

		setIsOpen(open);
		if (!open) {
			// If drawer is being closed, navigate back
			handleClose();
		}
	};

	// Determine drawer title and content based on mode
	const title = userById.data?.displayName
		? userById.data?.displayName
		: `${userById.data?.firstName || "User"} ${userById.data?.lastName || ""}`;
	const description = `User details and settings`;
	const tabs = [
		{ label: "Details", id: "details" },
		{ label: "Organizations", id: "organizations" },
		{ label: "Activity", id: "activity" },
	];

	// Get the current tab info and create edit mode tabs
	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [{ label: `Edit ${currentTabInfo.label}`, id: currentTabInfo.id }]
		: [];

	const statusTranslations = {
		Active: "Active",
		Suspended: "Suspended",
		Archived: "Archived",
		Retired: "Retired",
	} as const;

	useEffect(() => {
		if (
			currentTab !== "details" ||
			search.mode !== "edit" ||
			editAccessPending ||
			editAccess.canEdit
		) {
			return;
		}
		navigate({
			to: "/admin/users/organization/$userId/details",
			params: { userId: params.userId },
			search: { mode: "view" },
			replace: true,
		});
	}, [
		currentTab,
		editAccess.canEdit,
		editAccessPending,
		navigate,
		params.userId,
		search.mode,
	]);

	const editOrgDetailsForm = useEditOrgUserDetailsForm(params.userId, () => {
		// Success callback - navigate back to view mode to show success message
		navigate({
			to: "/admin/users/organization/$userId/details",
			params: { userId: params.userId },
			search: { mode: "view" },
		});
	});

	const hideTabs =
		search.organizationId &&
		search.mode === "edit" &&
		currentTab === "organizations";
	const showEditChrome = search.mode === "edit" && currentTab !== "details";

	return (
		<DrawerProvider>
			{/* Custom overlay for edit mode */}
			{showOverlay && (
				<div
					className={`fixed inset-0 bg-overlay/70 z-30 transition-opacity duration-100 ${
						search.mode === "edit" && isOverlayAnimating
							? "animate-in fade-in"
							: search.mode === "view" && isOverlayAnimating
								? "animate-out fade-out"
								: "opacity-100"
					}`}
					role="presentation"
					aria-hidden="true"
					onClick={(e) => {
						// Prevent closing when clicking overlay in edit mode
						e.preventDefault();
						e.stopPropagation();
					}}
					onKeyDown={(e) => {
						// Prevent closing when pressing keys on overlay in edit mode
						e.preventDefault();
						e.stopPropagation();
					}}
				/>
			)}
			<Drawer
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				width="40%"
				// Stay in transparent mode to prevent re-animation
				backdropPreset="transparent"
				dialogClassName="gap-0 overflow-hidden"
			>
				{({ close }) => (
					<>
						<editOrgDetailsForm.AppForm>
							<Drawer.Header
								variant="document"
								showDivider={false}
								icon={
									<Avatar
										size="md"
										src={userAvatarSrc || undefined}
										initials={initials}
									/>
								}
								iconSize="md"
								iconTheme="light"
								title={
									<div className="flex items-center flex-row gap-4">
										<span>{title}</span>
										{userById.data?.status.name && (
											<BadgeWithDot
												type="modern"
												size="sm"
												color={
													userById.data?.status.name
														? statusColors[
																userById.data?.status
																	.name as keyof typeof statusColors
															]
														: "gray"
												}
											>
												{statusTranslations[
													userById.data?.status
														.name as keyof typeof statusTranslations
												] || userById.data?.status.name}
											</BadgeWithDot>
										)}
									</div>
								}
								description={description}
								showCloseButton={!showEditChrome}
								closeButtonClassName="absolute top-2 right-2 z-10"
								onClose={() => {
									close();
									handleClose();
								}}
							/>
							<Drawer.ScrollBody showPlaceholder={false}>
								{userById.isLoading ? (
									<div className="flex w-full flex-1 items-center justify-center">
										<LoadingIndicator
											type="line-simple"
											size="xl"
											supportingText
										/>
									</div>
								) : userById.isError ? (
									<div className="flex flex-col items-center justify-center h-full gap-4">
										<Illustration type="cloud" size="sm">
											<div className="bg-error-500 rounded-full p-3">
												<AlertCircle className="size-6" />
											</div>
										</Illustration>
										<div className="flex flex-col items-center justify-center pt-4">
											<p className="text-gray-900 font-semibold text-md">
												{t("admin.organizations.errors.errorLoadingPage")}
											</p>
											<div className="w-[60%]">
												<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
													{t("admin.organizations.errors.errorLoadingContent")}
												</p>
											</div>
										</div>
									</div>
								) : (
									<div className="flex w-full flex-col">
										<NativeSelect
											aria-label="Tabs"
											value={currentTab}
											onChange={(event) =>
												navigate({
													to: `/admin/users/organization/$userId/${event.target.value}`,
													params: { userId: params.userId },
													search: {
														mode: search.mode,
													},
												})
											}
											options={(showEditChrome ? editModeTabs : tabs).map((tab) => ({
												label: tab.label,
												value: tab.id,
											}))}
											className="mx-6 mt-3 w-80 md:hidden"
										/>
										{!hideTabs && (
											<div className="flex h-12 w-full shrink-0 flex-col items-stretch border-b border-secondary bg-primary px-(--spacing-3xl,24px) max-md:hidden">
												<Tabs
													selectedKey={currentTab}
													onSelectionChange={(event) =>
														navigate({
															to: `/admin/users/organization/$userId/${event}`,
															params: {
																userId: params.userId,
															},
															search: {
																mode: search.mode,
															},
														})
													}
													className="h-full w-full"
												>
													<TabList
														type="underline"
														size="sm"
														items={showEditChrome ? editModeTabs : tabs}
														className="h-full w-full items-stretch gap-(--spacing-lg,12px) border-b-0"
													>
														{(tab) => (
															<Tab
																id={tab.id}
																label={tab.label}
																className="h-full items-end -mb-px"
															/>
														)}
													</TabList>
												</Tabs>
											</div>
										)}
										<div className="flex w-full flex-col gap-(--spacing-xs,4px) p-(--spacing-xl,16px)">
											<TabContentWrapper tabKey={currentTab}>
												<Outlet />
											</TabContentWrapper>
										</div>
									</div>
								)}
							</Drawer.ScrollBody>
							{/* Drawer Footer - Renders based on state from child routes */}
							<DrawerFooterWithState
								currentTab={currentTab}
								searchMode={
									currentTab === "details" ? "view" : search.mode
								}
								params={params}
								hideTabs={hideTabs}
								editOrgDetailsForm={editOrgDetailsForm}
								close={close}
								handleClose={handleClose}
								navigate={navigate}
								t={t}
							/>
						</editOrgDetailsForm.AppForm>
					</>
				)}
			</Drawer>
		</DrawerProvider>
	);
}

/**
 * Drawer Footer Component
 * Consumes state from child routes via DrawerContext and renders appropriate footer
 */
function DrawerFooterWithState({
	currentTab,
	searchMode,
	params,
	hideTabs,
	editOrgDetailsForm,
	close: _close,
	handleClose,
	navigate,
	t,
}: {
	currentTab: Tab;
	searchMode: Mode;
	params: { userId: string };
	hideTabs: boolean | string | undefined;
	editOrgDetailsForm: any;
	close: () => void;
	handleClose: () => void;
	navigate: any;
	t: any;
}) {
	void _close;
	// Get state from drawer context (pushed up from child routes)
	const errors = useDrawerStore((state) => state.errors);
	const successMessages = useDrawerStore((state) => state.successMessages);
	const _canSubmit = useDrawerStore((state) => state.canSubmit);
	const _isDirty = useDrawerStore((state) => state.isDirty);
	const _isSubmitting = useDrawerStore((state) => state.isSubmitting);
	const _handleSave = useDrawerStore((state) => state.handleSave);
	const _handleCancel = useDrawerStore((state) => state.handleCancel);
	void _canSubmit;
	void _isDirty;
	void _isSubmitting;
	void _handleSave;
	void _handleCancel;
	const clearErrors = useDrawerStore((state) => state.clearErrors);
	const clearSuccessMessages = useDrawerStore(
		(state) => state.clearSuccessMessages,
	);

	const routerLocation = useLocation();

	// biome-ignore lint/correctness/useExhaustiveDependencies: Zustand store functions are stable, and location/tab values are intentional triggers for clearing
	useEffect(() => {
		// Clear when route or viewed user changes (DrawerFooterWithState uses router location, not window).
		clearErrors();
		clearSuccessMessages();
	}, [
		routerLocation.pathname,
		routerLocation.search,
		currentTab,
		searchMode,
		params.userId,
	]);

	// Details tab in edit mode
	if (currentTab === "details" && searchMode === "edit") {
		return (
			<editOrgDetailsForm.Subscribe
				selector={(s: any) => [s.canSubmit, s.isDirty, s.isSubmitting]}
			>
				{([formCanSubmit, formIsDirty, formIsSubmitting]: [
					boolean,
					boolean,
					boolean,
				]) => (
					<Drawer.Footer>
						<div className="flex flex-col gap-3 w-full">
							<div className="flex items-center justify-between gap-3 w-full">
								<div className="flex flex-col gap-2 flex-1 min-w-0 overflow-hidden">
									{editOrgDetailsForm.successMessages.length > 0 && (
										<div className="flex flex-wrap gap-2">
											{editOrgDetailsForm.successMessages.map(
												(message: string) => (
													<BadgeGroup
														key={`success-${message}`}
														addonText="Success"
														theme="light"
														color="success"
														size="lg"
														showCloseButton={true}
														onClose={() =>
															editOrgDetailsForm.clearSuccessMessages()
														}
														addonClassName="font-bold text-xs capitalize tracking-wide"
													>
														{"User updated"}
													</BadgeGroup>
												),
											)}
										</div>
									)}
									{editOrgDetailsForm.errors.length > 0 && (
										<div className="flex flex-wrap gap-2">
											{editOrgDetailsForm.errors.map((error: string) => (
												<BadgeGroup
													key={`error-${error}`}
													addonText="Error"
													theme="light"
													color="error"
													size="lg"
													showCloseButton={true}
													onClose={() => editOrgDetailsForm.clearErrors()}
													addonClassName="font-bold text-xs capitalize tracking-wide"
												>
													{"Failed to update user"}
												</BadgeGroup>
											))}
										</div>
									)}
								</div>

								{/* Right side - Action buttons */}
								<div className="flex items-center gap-3">
									<Button
										color="secondary"
										size="md"
										onClick={() => {
											navigate({
												to: "/admin/users/organization/$userId/details",
												params: { userId: params.userId },
												search: { mode: "view" },
											});
											editOrgDetailsForm.reset();
										}}
									>
										{t("common.actions.cancelEdits")}
									</Button>
									<Button
										color="primary"
										size="md"
										isLoading={formIsSubmitting}
										isDisabled={!formCanSubmit || !formIsDirty}
										onClick={() => {
											if (formCanSubmit && formIsDirty) {
												editOrgDetailsForm.handleSubmit();
											}
										}}
									>
										{t("common.actions.saveEdits")}
									</Button>
								</div>
							</div>
						</div>
					</Drawer.Footer>
				)}
			</editOrgDetailsForm.Subscribe>
		);
	}

	// View mode footer (default)
	return (
		<Drawer.Footer>
			<div className="flex flex-col gap-3 w-full">
				{/* Success/Error messages in view mode */}
				{/* {(editOrgDetailsForm.successMessages.length > 0 ||
					editOrgDetailsForm.errors.length > 0 ||
					successMessages.length > 0 ||
					errors.length > 0) && (
					<div className="flex flex-col gap-2">

					</div>
				)} */}
				<div className="flex items-center justify-between w-full">
					<div className="flex-1">
						{editOrgDetailsForm.successMessages.length > 0 && (
							<div className="flex flex-wrap">
								{editOrgDetailsForm.successMessages.map((message: string) => (
									<BadgeGroup
										key={`success-${message}`}
										addonText="Success"
										theme="light"
										color="success"
										size="lg"
										showCloseButton={true}
										onClose={() => editOrgDetailsForm.clearSuccessMessages()}
										addonClassName="font-bold text-xs tracking-wide"
									>
										{"User updated"}
									</BadgeGroup>
								))}
							</div>
						)}

						{/* Show drawer context success messages */}
						{successMessages.length > 0 && (
							<div className="flex flex-wrap">
								{successMessages.map((message) => (
									<BadgeGroup
										key={`success-${message}`}
										addonText="Success"
										theme="light"
										color="success"
										size="lg"
										showCloseButton={true}
										onClose={clearSuccessMessages}
										addonClassName="font-bold text-xs tracking-wide"
									>
										{message}
									</BadgeGroup>
								))}
							</div>
						)}

						{/* Show form errors */}
						{editOrgDetailsForm.errors.length > 0 && (
							<div className="flex flex-wrap">
								{editOrgDetailsForm.errors.map((error: string) => (
									<BadgeGroup
										key={`error-${error}`}
										addonText="Error"
										theme="light"
										color="error"
										size="lg"
										showCloseButton={true}
										onClose={() => editOrgDetailsForm.clearErrors()}
										addonClassName="font-bold text-xs tracking-wide"
									>
										{"Failed to update user"}
									</BadgeGroup>
								))}
							</div>
						)}

						{/* Show drawer context errors */}
						{errors.length > 0 && (
							<div className="flex flex-wrap">
								{errors.map((error) => (
									<BadgeGroup
										key={`error-${error}`}
										addonText="Error"
										theme="light"
										color="error"
										size="lg"
										showCloseButton={true}
										onClose={clearErrors}
										addonClassName="font-bold text-xs tracking-wide text-white bg-error-600 border-none"
									>
										{error}
									</BadgeGroup>
								))}
							</div>
						)}
					</div>
					<div className="flex items-center gap-3">
						<Button
							color="secondary"
							size="md"
							onClick={handleClose}
							isDisabled={!!hideTabs}
						>
							{t("common.actions.close")}
						</Button>
					</div>
				</div>
			</div>
		</Drawer.Footer>
	);
}
